class LayloTopHitsTabs extends HTMLElement {
  connectedCallback() {
    this._nav    = this.querySelector('.laylo-top-hits__tab-nav');
    /** @type {NodeListOf<HTMLElement>} */
    this._panels = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.laylo-top-hits__category'));
    /** @type {NodeListOf<HTMLElement>} */
    this._tabs   = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[role="tab"]'));

    if (!this._nav || !this._panels.length || !this._tabs.length) return;

    // JS owns panel visibility — hide all panels immediately before first
    // activation. Without JS, panels render visible and stacked (no-JS state).
    // This single write happens before any paint from _activate(), so there
    // is no visible flash of all panels showing before the first tab activates.
    this._panels.forEach(panel => { panel.hidden = true; });

    // Activate the first tab on init
    const firstTab = this._tabs[0];
    if (firstTab) this._activate(firstTab);

    // Single delegated listener on the nav container
    this._nav.addEventListener('click', (e) => {
      const tab = /** @type {HTMLElement | null} */ (/** @type {Element} */ (e.target)?.closest('[role="tab"]'));
      if (!tab) return;
      this._activate(tab);
    });

    // Keyboard navigation — arrow keys move between tabs
    this._nav.addEventListener('keydown', (e) => {
      const ke  = /** @type {KeyboardEvent} */ (e);
      const tabs = [...(this._tabs ?? [])];
      const current = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      if (current === -1) return;

      let next = -1;
      if (ke.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (ke.key === 'ArrowLeft')  next = (current - 1 + tabs.length) % tabs.length;
      if (ke.key === 'Home')       next = 0;
      if (ke.key === 'End')        next = tabs.length - 1;

      if (next === -1) return;
      ke.preventDefault();
      const nextTab = tabs[next];
      if (!nextTab) return;
      nextTab.focus();
      this._activate(nextTab);
    });
  }

  /**
   * Activate a tab and its corresponding panel.
   * All DOM reads happen before any writes to avoid layout thrashing.
   * @param {HTMLElement} activeTab
   */
  _activate(activeTab) {
    const targetId = activeTab.dataset.target;

    // Batch reads
    const tabs   = [...(this._tabs   ?? [])];
    const panels = [...(this._panels ?? [])];

    // Batch writes
    tabs.forEach(tab => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.classList.toggle('is-active', isActive);
      // tabindex: -1 on inactive tabs — keyboard focus only on active tab
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach(panel => {
      const isActive = panel.id === targetId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });
  }
}

customElements.define('laylo-top-hits-tabs', LayloTopHitsTabs);
